import { useEffect, useRef } from 'react';

export default function useMulnyangGameAudio() {
  const wrongAudioCtxRef = useRef(null);
  const effectAudioCtxRef = useRef(null);

  useEffect(() => {
    return () => {
      if (wrongAudioCtxRef.current && typeof wrongAudioCtxRef.current.close === 'function') {
        wrongAudioCtxRef.current.close().catch(() => {});
      }
      if (effectAudioCtxRef.current && typeof effectAudioCtxRef.current.close === 'function') {
        effectAudioCtxRef.current.close().catch(() => {});
      }
      wrongAudioCtxRef.current = null;
      effectAudioCtxRef.current = null;
    };
  }, []);

  const playWrongSound = () => {
    try {
      if (!wrongAudioCtxRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        wrongAudioCtxRef.current = new AudioContext();
      }
      const ctx = wrongAudioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(200, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(150, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch (error) {
      // Ignore audio failures in constrained environments.
    }
  };

  const getEffectAudioCtx = () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    if (!effectAudioCtxRef.current) effectAudioCtxRef.current = new AudioContext();
    const ctx = effectAudioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  };

  const playNote = (freq, startDelay = 0, duration = 0.12, gainValue = 0.12, type = 'sine') => {
    try {
      const ctx = getEffectAudioCtx();
      if (!ctx) return;
      const startAt = ctx.currentTime + startDelay;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, startAt);
      gain.gain.setValueAtTime(gainValue, startAt);
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + duration);
    } catch (error) {
      // Ignore audio failures in constrained environments.
    }
  };

  const playClearSfx = (difficulty) => {
    if (difficulty === 'easy') {
      playNote(523, 0.0, 0.12, 0.15, 'sine');
      playNote(659, 0.08, 0.12, 0.12, 'sine');
      playNote(784, 0.16, 0.2, 0.1, 'sine');
      return;
    }
    if (difficulty === 'normal') {
      playNote(523, 0.0, 0.1, 0.15, 'sine');
      playNote(659, 0.07, 0.1, 0.13, 'sine');
      playNote(784, 0.14, 0.1, 0.12, 'sine');
      playNote(1047, 0.21, 0.25, 0.1, 'sine');
      return;
    }
    if (difficulty === 'hard') {
      playNote(392, 0.0, 0.08, 0.18, 'square');
      playNote(523, 0.06, 0.08, 0.16, 'sine');
      playNote(659, 0.12, 0.08, 0.14, 'sine');
      playNote(784, 0.18, 0.1, 0.13, 'sine');
      playNote(1047, 0.25, 0.1, 0.12, 'sine');
      playNote(1319, 0.33, 0.3, 0.1, 'sine');
      return;
    }
    playNote(262, 0.0, 0.06, 0.2, 'square');
    playNote(392, 0.05, 0.06, 0.18, 'square');
    playNote(523, 0.1, 0.07, 0.16, 'sine');
    playNote(659, 0.16, 0.07, 0.15, 'sine');
    playNote(784, 0.22, 0.08, 0.14, 'sine');
    playNote(988, 0.28, 0.08, 0.13, 'sine');
    playNote(1175, 0.35, 0.1, 0.12, 'sine');
    playNote(1319, 0.42, 0.1, 0.11, 'triangle');
    playNote(1568, 0.5, 0.35, 0.1, 'sine');
  };

  const playStreakSfx = () => {
    playNote(131, 0.0, 0.15, 0.2, 'square');
    playNote(165, 0.08, 0.12, 0.18, 'square');
    playNote(262, 0.14, 0.1, 0.2, 'square');
    playNote(330, 0.2, 0.1, 0.18, 'sine');
    playNote(392, 0.26, 0.08, 0.17, 'sine');
    playNote(523, 0.32, 0.08, 0.16, 'sine');
    playNote(659, 0.38, 0.08, 0.15, 'sine');
    playNote(784, 0.44, 0.1, 0.14, 'sine');
    playNote(988, 0.52, 0.1, 0.13, 'sine');
    playNote(1047, 0.6, 0.1, 0.12, 'sine');
    playNote(1319, 0.68, 0.12, 0.11, 'sine');
    playNote(1568, 0.78, 0.15, 0.1, 'triangle');
    playNote(1760, 0.88, 0.2, 0.1, 'sine');
    playNote(2093, 1.0, 0.5, 0.09, 'sine');
  };

  const playCoinSfx = () => {
    try {
      const ctx = getEffectAudioCtx();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(988, ctx.currentTime);
      osc.frequency.setValueAtTime(1319, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.25);
    } catch (error) {
      // Ignore audio failures in constrained environments.
    }
  };

  return {
    playWrongSound,
    playClearSfx,
    playStreakSfx,
    playCoinSfx,
  };
}
